import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import CourtPointsCard from './CourtPointsCard';
import FinalizePreviewDialog from './FinalizePreviewDialog';
import ReopenWeekDialog from './ReopenWeekDialog';
import EditAssignmentsDialog from './EditAssignmentsDialog';
import { CaretLeft, CaretRight, FloppyDisk, CheckCircle, ArrowCounterClockwise, Lock, ArrowsLeftRight } from '@phosphor-icons/react';

// Owns the week's slot edits in memory, batches saves, drives finalize.
// `currentWeek` is the canonical "playable" week (from session.current_week).
// `viewWeek` is what the admin is looking at; when viewWeek < currentWeek the
// tab is read-only (past, already finalized).
export default function WeekManagementTab({ session, onSessionChange }) {
  const currentWeek = session.current_week;
  const weeksPlanned = session.weeks_planned;
  const [viewWeek, setViewWeek] = useState(currentWeek);
  const [weeksList, setWeeksList] = useState([]);
  const [week, setWeek] = useState(null);            // { week, courts }
  const [courts, setCourts] = useState([]);          // local editable courts
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingTies, setPendingTies] = useState([]);
  const [tiePicks, setTiePicks] = useState({}); // `${court}-${role}` -> registration_id
  const [reopenOpen, setReopenOpen] = useState(false);
  const [editAssignOpen, setEditAssignOpen] = useState(false);

  // Stale-response guard: only the newest reload may apply, and a reload whose
  // response arrives after the admin has started editing is discarded so it
  // can't wipe in-progress scores / tie picks.
  const reloadSeqRef = useRef(0);   // bumped each time a reload is issued
  const editSeqRef = useRef(0);     // bumped on every local score/tie edit

  // Past weeks (week_number < currentWeek) are always finalized → read-only.
  // The "active" week is currentWeek; can be in_progress or submitted.
  const isPast = viewWeek < currentWeek;
  const isFuture = viewWeek > currentWeek;
  const isActive = viewWeek === currentWeek && !isFuture;
  const sessionDone = session.status === 'completed';
  const readOnly = isPast || isFuture || sessionDone;

  const reloadWeek = useCallback(async () => {
    if (isFuture || viewWeek < 1) {
      setWeek(null);
      setCourts([]);
      return;
    }
    const seq = ++reloadSeqRef.current;
    const editAtStart = editSeqRef.current;
    try {
      const r = await eventAPI.getWeek(session.id, viewWeek);
      // Discard if a newer reload superseded this one, or if the admin edited
      // scores/ties while this request was in flight.
      if (seq !== reloadSeqRef.current || editSeqRef.current !== editAtStart) return;
      setWeek(r.data);
      setCourts(r.data.courts || []);
      setDirty(false);
      setPendingTies([]);
      setTiePicks({});
    } catch (err) {
      if (seq !== reloadSeqRef.current || editSeqRef.current !== editAtStart) return;
      setWeek(null);
      setCourts([]);
    }
  }, [session.id, viewWeek, isFuture]);

  const reloadWeeksList = useCallback(async () => {
    try {
      const r = await eventAPI.listWeeks(session.id);
      setWeeksList(r.data?.weeks || []);
    } catch {
      setWeeksList([]);
    }
  }, [session.id]);

  useEffect(() => { reloadWeek(); }, [reloadWeek]);
  useEffect(() => { reloadWeeksList(); }, [reloadWeeksList]);

  // Keep viewWeek in sync if session advances under us
  useEffect(() => {
    if (viewWeek > currentWeek) setViewWeek(currentWeek);
  }, [currentWeek, viewWeek]);

  const onSlotChange = (slotId, patch) => {
    editSeqRef.current += 1;
    setCourts((prev) =>
      prev.map((c) => ({
        ...c,
        slots: c.slots.map((s) => (s.id === slotId ? { ...s, ...patch } : s)),
      })),
    );
    setDirty(true);
    // A score edit can change who is tied — drop any prior picks so nothing stale is kept.
    setTiePicks({});
    if (pendingTies.length) setPendingTies([]);
  };

  const handleSaveDraft = async () => {
    if (!isActive || busy) return;
    setBusy(true);
    const editAtStart = editSeqRef.current;
    const slots = courts.flatMap((c) =>
      c.slots.map((s) => ({
        slot_id: s.id,
        points: s.no_show ? 0 : (s.points ?? null),
        sub_profile_id: s.sub_profile_id || null,
        is_substitute: !!s.is_substitute && !s.no_show,
        no_show: !!s.no_show,
        notes: s.notes || null,
      })),
    );
    try {
      const r = await eventAPI.saveWeekPoints(session.id, viewWeek, slots);
      toast.success(`Saved (${r.data.slots_updated} slots).`);
      // Only sync from the server if the admin didn't edit while the save was
      // in flight — otherwise the fresh reload would wipe the newer, still
      // unsaved edit (the admin stays "dirty" and can save again).
      if (editSeqRef.current === editAtStart) {
        setDirty(false);
        await reloadWeek();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save points');
    } finally {
      setBusy(false);
    }
  };

  const handleFinalize = async (tieBreakers) => {
    if (!isActive || busy) return;
    setBusy(true);
    try {
      // Always save the current draft first, so the backend has the latest
      // points before computing movement.
      if (dirty) {
        await handleSaveDraftInline();
      }
      const r = await eventAPI.finalizeWeek(session.id, viewWeek, tieBreakers);
      const body = r.data;
      toast.success(
        body.session_completed
          ? `Week ${viewWeek} finalized — session complete!`
          : `Week ${viewWeek} finalized.`,
      );
      setPreviewOpen(false);
      setPendingTies([]);
      setTiePicks({});
      // Reload session + weeks list; viewWeek bumps via the effect
      await onSessionChange?.();
      await reloadWeeksList();
      setViewWeek((w) => (body.session_completed ? w : w + 1));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === 'tie_detected') {
        // Inline tie resolution — merge newly-reported ties with any already
        // shown, preserving existing picks. No modal/popup.
        const incoming = detail.ties || [];
        setPendingTies((prev) => {
          const byKey = {};
          [...prev, ...incoming].forEach((t) => { byKey[`${t.court_number}-${t.role}`] = t; });
          return Object.values(byKey);
        });
        setPreviewOpen(false);
        toast.message('Tie detected — tap ↑ UP / ↓ DOWN beside the highlighted players, then Finalize.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not finalize');
      }
    } finally {
      setBusy(false);
    }
  };

  // helper to inline the save for finalize-flow (no toast)
  const handleSaveDraftInline = async () => {
    const slots = courts.flatMap((c) =>
      c.slots.map((s) => ({
        slot_id: s.id,
        points: s.no_show ? 0 : (s.points ?? null),
        sub_profile_id: s.sub_profile_id || null,
        is_substitute: !!s.is_substitute && !s.no_show,
        no_show: !!s.no_show,
        notes: s.notes || null,
      })),
    );
    await eventAPI.saveWeekPoints(session.id, viewWeek, slots);
    setDirty(false);
  };

  const incomplete = courts.some((c) => c.slots.some((s) => s.points === null || s.points === undefined));

  // ---- Live tie detection (mirrors backend finalize gating) ----
  // Buttons appear automatically as a court's scores complete — no need to
  // click Review & finalize first. The backend remains authoritative on save.
  const liveTies = useMemo(() => {
    const numCourts = session.num_courts;
    const locked = new Set((courts || []).filter((c) => c.movement_locked).map((c) => c.court_number));
    const out = [];
    for (const c of (courts || [])) {
      const slots = c.slots || [];
      if (slots.length === 0 || slots.some((s) => s.points === null || s.points === undefined)) continue;
      const sorted = [...slots].sort((a, b) => (b.points ?? -1) - (a.points ?? -1));
      const nonSub = sorted.filter((s) => !s.is_substitute);
      const isLocked = locked.has(c.court_number);
      const upPossible = c.court_number !== 1 && !isLocked && !locked.has(c.court_number - 1);
      const downPossible = c.court_number !== numCourts && !isLocked && !locked.has(c.court_number + 1);
      let highWinner = null;
      if (upPossible && nonSub.length) {
        const hp = nonSub[0].points;
        const highs = nonSub.filter((s) => s.points === hp);
        if (highs.length > 1) out.push({ court_number: c.court_number, role: 'highest', tied_registration_ids: highs.map((s) => s.registration_id), points: hp });
        else highWinner = highs[0].registration_id;
      }
      if (downPossible && sorted.length) {
        const lp = sorted[sorted.length - 1].points;
        const lows = sorted.filter((s) => s.points === lp && s.registration_id !== highWinner);
        if (lows.length > 1) out.push({ court_number: c.court_number, role: 'lowest', tied_registration_ids: lows.map((s) => s.registration_id), points: lp });
      }
    }
    return out;
  }, [courts, session.num_courts]);

  // Merge the backend's 409 ties (safety net) with the live ones.
  const displayTies = useMemo(() => {
    const byKey = {};
    [...liveTies, ...(pendingTies || [])].forEach((t) => { byKey[`${t.court_number}-${t.role}`] = t; });
    return Object.values(byKey);
  }, [liveTies, pendingTies]);

  const tiesByCourt = useMemo(() => {
    const m = {};
    displayTies.forEach((t) => { (m[t.court_number] = m[t.court_number] || []).push(t); });
    return m;
  }, [displayTies]);

  const unresolvedTieCount = displayTies.filter((t) => !tiePicks[`${t.court_number}-${t.role}`]).length;
  const hasUnresolvedTies = unresolvedTieCount > 0;

  const handleTiePick = (key, regId) => {
    editSeqRef.current += 1;
    setTiePicks((p) => ({ ...p, [key]: regId }));
  };
  const buildTieBreakers = () => displayTies.map((t) => ({
    court_number: t.court_number,
    role: t.role,
    registration_id: tiePicks[`${t.court_number}-${t.role}`],
  }));

  // Edit Assignments is allowed on any unfinalized week. If scoring data
  // already exists, the dialog shows an inline warning and a Save confirm
  // step. The backend preserves per-player scoring data on edit.
  const anyScoringStarted = courts.some((c) => c.slots.some((s) => (
    (s.points !== null && s.points !== undefined)
    || s.is_substitute
    || s.sub_profile_id
    || s.no_show
    || (s.notes && String(s.notes).trim())
  )));
  const canEditAssignments = !readOnly && week && courts.length > 0;

  const finalizedWeeks = useMemo(
    () => new Set(weeksList.filter((w) => w.status === 'finalized').map((w) => w.week_number)),
    [weeksList],
  );

  // The only reopenable week is the highest-numbered finalized week.
  // Older finalized weeks are locked because a later week is finalized after them.
  const latestReopenableWeek = useMemo(() => {
    if (finalizedWeeks.size === 0) return null;
    return Math.max(...finalizedWeeks);
  }, [finalizedWeeks]);

  const currentWeekStatus = week?.week?.status;
  const viewedWeekIsLatestFinalized =
    currentWeekStatus === 'finalized' && viewWeek === latestReopenableWeek;
  const viewedWeekIsLockedFinalized =
    currentWeekStatus === 'finalized' && latestReopenableWeek !== null && viewWeek < latestReopenableWeek;

  const handleReopen = async (reason) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await eventAPI.reopenWeek(session.id, viewWeek, reason);
      const body = r.data;
      toast.success(
        body.next_week_deleted
          ? `Week ${viewWeek} reopened. Next-week courts cleared.`
          : `Week ${viewWeek} reopened.`,
      );
      setReopenOpen(false);
      await onSessionChange?.();
      await reloadWeek();
      await reloadWeeksList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reopen week');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="week-management-tab">
      {/* Week selector */}
      <div className="flex items-center justify-between bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl px-3 py-2" data-testid="week-selector">
        <button
          onClick={() => setViewWeek((w) => Math.max(1, w - 1))}
          disabled={viewWeek <= 1}
          data-testid="week-prev-btn"
          className="p-2 rounded-md border border-[#e3dcd1] text-[#6a766e] disabled:opacity-40"
        >
          <CaretLeft size={14} />
        </button>
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Week</div>
          <div className="font-serif text-2xl text-[#013523]" data-testid="week-number-display">
            {viewWeek} / {weeksPlanned}
          </div>
          <div className="text-[10px] uppercase tracking-wider mt-0.5">
            {sessionDone && viewWeek === currentWeek
              ? <span className="text-[#015e3f]">final</span>
              : isPast
                ? <span className="text-[#6a766e]">finalized · read-only</span>
                : isActive
                  ? <span className="text-[#015e3f]">active</span>
                  : <span className="text-[#6a766e]">not started</span>}
          </div>
        </div>
        <button
          onClick={() => setViewWeek((w) => Math.min(currentWeek, w + 1))}
          disabled={viewWeek >= currentWeek}
          data-testid="week-next-btn"
          className="p-2 rounded-md border border-[#e3dcd1] text-[#6a766e] disabled:opacity-40"
        >
          <CaretRight size={14} />
        </button>
      </div>

      {/* Quick week chips */}
      {weeksList.length > 1 && (
        <div className="flex gap-1 flex-wrap" data-testid="week-chips">
          {weeksList.map((w) => {
            const isFinalized = finalizedWeeks.has(w.week_number);
            const isLocked = isFinalized && latestReopenableWeek !== null && w.week_number < latestReopenableWeek;
            return (
              <button
                key={w.id}
                data-testid={`week-chip-${w.week_number}`}
                onClick={() => setViewWeek(w.week_number)}
                title={isLocked ? 'A later week is finalized. Reopen later weeks first.' : ''}
                className={`text-[11px] px-2.5 py-1 rounded-full border flex items-center gap-1 ${
                  viewWeek === w.week_number
                    ? 'bg-[#015e3f] text-white border-[#015e3f]'
                    : isFinalized
                      ? 'bg-[#015e3f]/10 text-[#015e3f] border-[#015e3f]/30'
                      : 'bg-[#fbfaf8] text-[#013523] border-[#e3dcd1]'
                }`}
              >
                Wk {w.week_number}{isFinalized ? ' ✓' : ''}
                {isLocked && <Lock size={10} weight="fill" data-testid={`week-chip-locked-${w.week_number}`} />}
              </button>
            );
          })}
        </div>
      )}

      {/* Locked finalized week banner */}
      {viewedWeekIsLockedFinalized && (
        <div className="bg-[#f0ece3] border border-[#e3dcd1] rounded-2xl p-3 flex items-start gap-2" data-testid="locked-week-banner">
          <Lock size={16} weight="fill" className="text-[#6a766e] mt-0.5 shrink-0" />
          <div className="text-xs text-[#013523]">
            <div className="font-medium">Week {viewWeek} is locked</div>
            <div className="text-[#6a766e] mt-0.5">
              A later week has been finalized. Reopen Week {latestReopenableWeek} first if you need to correct earlier scores.
            </div>
          </div>
        </div>
      )}

      {/* Court cards */}
      {!week && (
        <div className="bg-[#f0ece3] border border-[#e3dcd1] rounded-2xl p-4 text-center text-sm text-[#6a766e]" data-testid="week-empty">
          {isFuture ? 'This week has not been seeded yet.' : 'No data for this week.'}
        </div>
      )}
      {week && courts.map((c) => (
        <CourtPointsCard
          key={c.id}
          court={c}
          sessionId={session.id}
          weekNumber={viewWeek}
          courtTies={tiesByCourt[c.court_number] || []}
          tiePicks={tiePicks}
          onTiePick={handleTiePick}
          onSlotChange={onSlotChange}
          onLockChange={(courtNumber, locked) => {
            // Local mirror so the finalize-preview banner is up-to-date
            // immediately without a full week refetch.
            editSeqRef.current += 1;
            setCourts((prev) => prev.map((x) => x.court_number === courtNumber ? { ...x, movement_locked: locked } : x));
          }}
          readOnly={readOnly}
        />
      ))}

      {/* Reopen action bar — only on the latest finalized week */}
      {week && viewedWeekIsLatestFinalized && (
        <div className="sticky-above-nav bg-[#fbfaf8]/95 backdrop-blur border border-[#e3dcd1] rounded-2xl p-3 flex items-center gap-2 shadow-sm" data-testid="reopen-action-bar">
          <div className="flex-1 text-xs text-[#6a766e]">
            Week {viewWeek} is finalized. Need to fix a score?
          </div>
          <button
            onClick={() => setReopenOpen(true)}
            disabled={busy}
            data-testid="reopen-week-btn"
            className="px-3 py-2 rounded-lg border border-amber-600 text-amber-700 bg-amber-50 hover:bg-amber-100 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowCounterClockwise size={14} weight="bold" /> Correct latest week
          </button>
        </div>
      )}

      {/* Action bar — active (in_progress/submitted) week */}
      {week && !readOnly && (
        <div className="sticky-above-nav bg-[#fbfaf8]/95 backdrop-blur border border-[#e3dcd1] rounded-2xl p-3 flex items-center gap-2 flex-wrap shadow-sm" data-testid="action-bar">
          <div className="flex-1 text-xs min-w-[120px]">
            {hasUnresolvedTies
              ? <span className="text-amber-700" data-testid="tie-hint">● Resolve the ↑/↓ tie picks to finalize</span>
              : dirty
                ? <span className="text-amber-700">● Unsaved changes</span>
                : <span className="text-[#6a766e]">All saved</span>}
          </div>
          <button
            onClick={() => setEditAssignOpen(true)}
            disabled={busy || !canEditAssignments}
            data-testid="edit-assignments-btn"
            title={!canEditAssignments ? 'Editing is blocked while the week is finalized or not started.' : (anyScoringStarted ? 'Scoring has started — you can still edit, but read the warning carefully.' : 'Move players between courts.')}
            className="px-3 py-2 rounded-lg border border-[#013523] text-[#013523] text-sm font-medium flex items-center gap-1 disabled:opacity-40"
          >
            <ArrowsLeftRight size={14} /> Edit assignments
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={busy || !dirty}
            data-testid="save-draft-btn"
            className="px-3 py-2 rounded-lg border border-[#015e3f] text-[#015e3f] text-sm font-medium flex items-center gap-1 disabled:opacity-50"
          >
            <FloppyDisk size={14} /> {busy ? 'Saving…' : 'Save draft'}
          </button>
          <button
            onClick={() => setPreviewOpen(true)}
            disabled={busy || incomplete || hasUnresolvedTies}
            data-testid="review-finalize-btn"
            className="px-3 py-2 rounded-lg bg-[#015e3f] text-white text-sm font-medium flex items-center gap-1 disabled:opacity-50"
          >
            <CheckCircle size={14} weight="fill" /> Review &amp; finalize
          </button>
        </div>
      )}

      <FinalizePreviewDialog
        open={previewOpen}
        weekNumber={viewWeek}
        numCourts={session.num_courts}
        courts={courts}
        busy={busy}
        onCancel={() => setPreviewOpen(false)}
        tieBreakers={buildTieBreakers()}
        onConfirm={() => handleFinalize(buildTieBreakers())}
      />

      <ReopenWeekDialog
        open={reopenOpen}
        weekNumber={viewWeek}
        nextWeekExists={weeksList.some((w) => w.week_number === viewWeek + 1)}
        sessionWasCompleted={sessionDone}
        busy={busy}
        onCancel={() => setReopenOpen(false)}
        onConfirm={handleReopen}
      />

      <EditAssignmentsDialog
        open={editAssignOpen}
        onClose={() => setEditAssignOpen(false)}
        session={session}
        week={week ? { week_number: viewWeek, courts } : null}
        scoringPresent={anyScoringStarted}
        onSaved={async () => { await reloadWeek(); }}
      />
    </div>
  );
}
